import HeadData from 'components/Common/HeadData'
import { scheduleIsCurrent } from 'lib/offer/scheduleStatusUtils'
import { useIntl } from 'react-intl'
import OfferGoogleProductDataScript from './OfferGoogleProductDataScript'
import HotelStructuredData from './HotelStructuredData'
import { LUXURY_PLUS } from 'luxPlus/constants/base'
import { getHeadDescription, getHeadTitle, isMetadataExperimentOn } from 'components/OfferPage/utils'
import { useLocation } from 'react-router-dom'
import { earlyAccessRestrictedToMembers } from 'luxPlus/lib/utils'
import { getImageUrl } from 'lib/image/imageUtils'

interface Props {
  offer: App.BundleOffer | App.HotelOffer | App.VillaOffer | App.TourOffer
  /**
   * There are exclusive LuxPlus+ offers. They should
   * appear as hidden offers to non-members
   */
  isLuxPlusHiddenOffer?: boolean
  /**
   * Set when the SPP renders: it emits its own `Hotel` JSON-LD (`PropertySppStructuredData`), and
   * every page must have exactly one `Hotel` block.
   */
  skipHotelStructuredData?: boolean
}

function OfferPageHeadData(props: Props) {
  const { offer, isLuxPlusHiddenOffer, skipHotelStructuredData } = props
  const intl = useIntl()
  const location = useLocation()

  const availableForPurchase = scheduleIsCurrent(offer.onlinePurchaseSchedule)
  const isMetadataExperiment = isMetadataExperimentOn(location.pathname)
  const usePrivacySafeMetadata = isLuxPlusHiddenOffer || offer.luxPlus.access === 'memberOnly' || earlyAccessRestrictedToMembers(offer)

  let title = ''
  let description = ''
  let imageId: string | undefined
  let imageUrl: string | undefined
  const offerImageId = offer.images[0]?.id

  if (usePrivacySafeMetadata) {
    title = intl.formatMessage(
      {
        id: 'wVe97_zZNAK7RmA_xoJ8',
        defaultMessage: '{programName} Member Exclusive',
        description: 'Page title for LuxPlus hidden hotel offers on the offer page',
      },
      { programName: LUXURY_PLUS.PROGRAM_NAME },
    )
    description = intl.formatMessage(
      {
        id: 'ZIloV-E7CKfkmHIsCSoF',
        defaultMessage: 'Unlock hidden hotel offers available exclusively for {programName} members. These escapes aren’t available anywhere else',
        description: 'Meta description for LuxPlus hidden hotel offers page',
      },
      { programName: LUXURY_PLUS.PROGRAM_NAME },
    )
    imageUrl = offerImageId ? getImageUrl(offerImageId, { width: 65, format: 'jpeg', quality: 'low' }) : undefined
  } else {
    title = getHeadTitle(offer, isMetadataExperiment)
    description = getHeadDescription(offer, isMetadataExperiment)
    imageId = offerImageId
  }

  return (
    <>
      <HeadData
        vertical={offer.type === 'rental' ? 'villas' : offer.type === 'tour' ? 'tours' : 'hotels'}
        restricted={usePrivacySafeMetadata}
        title={title}
        description={description}
        noIndex={offer.noIndex || !availableForPurchase}
        imageId={imageId}
        imageUrl={imageUrl}
      />
      {!usePrivacySafeMetadata && !skipHotelStructuredData && offer.property && <HotelStructuredData offer={offer} />}
      {!usePrivacySafeMetadata && <OfferGoogleProductDataScript
        availableForPurchase={availableForPurchase}
        offer={offer}
      />}
    </>
  )
}

export default OfferPageHeadData
